/* Reveal-on-scroll driver for the Benchmark landing sections.
   Content is visible by default; this script hides only elements clearly
   below the fold and reveals them as they enter the viewport, with
   scroll/timer fallbacks so nothing can stay hidden. */

(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // elements whose top is below this fraction of the viewport count as below the fold
  const FOLD = 0.95;

  const initReveal = () => {
    const revealables = Array.from(document.querySelectorAll('[data-reveal]')).filter(
      (el) => !el.classList.contains('in-view')
    );
    if (!revealables.length) return;

    if (reducedMotion || !('IntersectionObserver' in window)) {
      revealables.forEach((el) => el.classList.add('in-view'));
      return;
    }

    const reveal = (el) => {
      el.classList.add('in-view');
      el.classList.remove('pre-reveal');
    };

    // read all positions before writing any classes so the writes cannot
    // force a layout recalc per element
    const cutoffTop = window.innerHeight * FOLD;
    const tops = revealables.map((el) => el.getBoundingClientRect().top);
    const pending = [];
    revealables.forEach((el, i) => {
      // only elements clearly below the fold get hidden for the entrance
      if (tops[i] > cutoffTop) {
        el.classList.add('pre-reveal');
        pending.push(el);
      } else {
        el.classList.add('in-view');
      }
    });
    if (!pending.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.01, rootMargin: '0px 0px -8% 0px' }
    );
    pending.forEach((el) => io.observe(el));

    // belt-and-braces: a scroll/timer scan so nothing on screen stays hidden,
    // including carousel slides the observer never sees intersect
    let scanQueued = false;
    const timers = [];
    const scan = () => {
      scanQueued = false;
      const cutoff = window.innerHeight * FOLD;
      pending.forEach((el) => {
        if (!el.classList.contains('in-view') && el.getBoundingClientRect().top < cutoff) {
          reveal(el);
        }
      });
      if (pending.every((el) => el.classList.contains('in-view'))) {
        window.removeEventListener('scroll', queueScan);
        window.removeEventListener('resize', queueScan);
        timers.forEach(clearTimeout);
      }
    };
    const queueScan = () => {
      if (!scanQueued) {
        scanQueued = true;
        requestAnimationFrame(scan);
      }
    };
    window.addEventListener('scroll', queueScan, { passive: true });
    window.addEventListener('resize', queueScan, { passive: true });
    timers.push(setTimeout(scan, 400), setTimeout(scan, 1200));
  };

  // count-up for [data-count-up] values like "500M+" or "4.9"; text stays
  // as-is when it does not start with a number
  const initCounters = () => {
    const counters = Array.from(document.querySelectorAll('[data-count-up]')).filter(
      (el) => !el.dataset.countDone
    );
    if (!counters.length || reducedMotion || !('IntersectionObserver' in window)) return;

    // short enough that the number lands while the block is still on screen;
    // every value settles on exactly the text it was authored with
    const DURATION = 2500;

    const animate = (el, target, decimals, grouped, suffix) => {
      const start = performance.now();
      const format = (value) =>
        grouped
          ? value.toLocaleString('en-US', {
              minimumFractionDigits: decimals,
              maximumFractionDigits: decimals,
            })
          : value.toFixed(decimals);
      const step = (now) => {
        const progress = Math.min((now - start) / DURATION, 1);
        const eased = 1 - Math.pow(1 - progress, 1.6);
        el.textContent = format(target * eased) + suffix;
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          io.unobserve(el);
          const match = el.textContent.trim().match(/^(\d[\d,]*(?:\.\d+)?)(.*)$/);
          if (!match) return;
          const raw = match[1].replace(/,/g, '');
          const decimals = (raw.split('.')[1] || '').length;
          animate(el, parseFloat(raw), decimals, match[1].includes(','), match[2]);
        });
      },
      // start counting only once the number is properly on screen (above the
      // bottom 8% band, matching the reveal system's own margin), not the
      // instant it clips the viewport's bottom edge mid-scroll
      { threshold: 0.6, rootMargin: '0px 0px -8% 0px' }
    );
    counters.forEach((el) => {
      el.dataset.countDone = 'true';
      io.observe(el);
    });
  };

  const init = () => {
    initReveal();
    initCounters();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // re-scan when the theme editor injects a section
  document.addEventListener('shopify:section:load', init);
})();
